import { SavedBlockPoIsQuery } from '@/__generated__/graphql';
import {
  AnalysisCuboid,
  AnalysisCustomBlockWithFiles,
  CustomBlockDefectsByPaintBlock,
  CustomBlockQueryFilters,
} from '@/types';
import { StateMap } from '@/utils/stateMap';
import { atom } from 'jotai';
import atomWithDebounce from '@/utils/atomWithDebounce';

export const customBlockCuboids = atom<StateMap<string, AnalysisCuboid>>(
  new StateMap<string, AnalysisCuboid>()
);

export const showWorkscopeOverlayIcons = atom<boolean>(false);

export const selectedCustomBlock = atom<AnalysisCustomBlockWithFiles | undefined>(undefined);

export const hoveredCustomBlockId = atom<string | undefined>(undefined);

export const hoveredCustomBlock = atom<AnalysisCuboid | undefined>((get) => {
  const hoveredCustomBlockIdValue = get(hoveredCustomBlockId);
  const customBlockCuboidsValue = get(customBlockCuboids);

  if (!hoveredCustomBlockIdValue) return undefined;

  const found = customBlockCuboidsValue.get(hoveredCustomBlockIdValue);

  return found;
});

export const hoveredCustomBlockDefects = atom<
  CustomBlockDefectsByPaintBlock[0] | SavedBlockPoIsQuery['pointOfInterests'][0] | undefined
>((get) => {
  const hoveredCustomBlockValue = get(hoveredCustomBlock);
  if (!hoveredCustomBlockValue) return;

  const allCustomBlocksValue = get(allCustomBlocks);
  const found = allCustomBlocksValue?.find(
    (item) => item.paintRegionId === hoveredCustomBlockValue.id
  );

  // check for hovered saved block
  const allSavedBlocksValue = get(allSavedBlocks);
  const foundSavedBlock = allSavedBlocksValue?.find(
    (item) => item.id === hoveredCustomBlockValue.id
  );

  return found || foundSavedBlock;
});

export const customBlocksQueryInput = atom<{
  hasMoreItems: boolean;
  page: number;
  sortBy: string | undefined;
  sortDir: number | undefined;
}>({
  hasMoreItems: true,
  page: 2,
  // start with total area as the sort key
  // this assumes that total area is present as the key on the blocks
  sortBy: 'totalArea',
  sortDir: -1,
});

export const selectedSavedBlockId = atom<string | undefined>(undefined);
export const allSavedBlocks = atom<SavedBlockPoIsQuery['pointOfInterests'] | undefined>();
export const allCustomBlocks = atom<CustomBlockDefectsByPaintBlock | undefined>();

// Storing all saved blocks in a map for faster access.
// Used for:
// - Getting paint region of a saved block when drawing a POI overlay in deckplan overlay
export const allSavedBlocksMap = atom<
  Map<string, SavedBlockPoIsQuery['pointOfInterests'][0] | undefined>
>((get) => {
  const map = new Map<string, SavedBlockPoIsQuery['pointOfInterests'][0]>();
  const blocksList = get(allSavedBlocks);
  if (!blocksList) return map;

  for (const item of blocksList) {
    map.set(item.id, item);
  }

  return map;
});
export const selectedSavedBlock = atom<SavedBlockPoIsQuery['pointOfInterests'][0] | undefined>(
  (get) => get(allSavedBlocks)?.find((block) => block.id === get(selectedSavedBlockId))
);

export const customBlockQueryFilters = atom<CustomBlockQueryFilters>({
  excludeAlreadyPlannedBlocks: true,
});

export const customBlockSearchTermDebounced = atomWithDebounce<string>('', 500);
